/**
 * Minimal Server-Sent Events parser over a fetch body. `EventSource` cannot send an
 * Authorization header, so the chat stream is read with `fetch` instead.
 * Follows the WHATWG event-stream rules that matter here: CR/LF/CRLF line ends, comments,
 * multi-line `data`, sticky `id`, and an unterminated event at EOF is dropped.
 */

export interface SseMessage {
  event: string;
  data: string;
  id: string | undefined;
}

const LINE_END = /\r\n|\r|\n/;

export async function* parseSse(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<SseMessage, void, undefined> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let event = '';
  let data: string[] = [];
  let id: string | undefined;

  try {
    for (;;) {
      const { value, done } = await reader.read();
      buffer += done ? decoder.decode() : decoder.decode(value, { stream: true });

      for (;;) {
        const match = LINE_END.exec(buffer);
        if (!match) break;
        // A trailing CR may be the first half of CRLF split across chunks: wait for more.
        if (!done && match[0] === '\r' && match.index === buffer.length - 1) break;

        const line = buffer.slice(0, match.index);
        buffer = buffer.slice(match.index + match[0].length);

        if (line === '') {
          if (data.length > 0) {
            yield { event: event || 'message', data: data.join('\n'), id };
          }
          event = '';
          data = [];
          continue;
        }
        if (line.startsWith(':')) continue;

        const colon = line.indexOf(':');
        const field = colon === -1 ? line : line.slice(0, colon);
        let fieldValue = colon === -1 ? '' : line.slice(colon + 1);
        if (fieldValue.startsWith(' ')) fieldValue = fieldValue.slice(1);

        if (field === 'event') event = fieldValue;
        else if (field === 'data') data.push(fieldValue);
        else if (field === 'id' && !fieldValue.includes('\0')) id = fieldValue;
      }

      if (done) return;
    }
  } finally {
    reader.releaseLock();
  }
}
